import moment from 'moment-timezone';
import { useCallback, useContext, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';
import useRequest from 'hooks/useRequest';
import EventService from 'services/api/EventService';
import EventRegistrationService from 'services/api/EventRegistrationService';
import UserService from 'services/api/UserService';
import Country from 'store/types/Country';
import SelectOption from 'store/types/SelectOption';
import { ParametersContext } from 'components/ParametersGuard';
import { EVENT_CATALOG_FILTER_KEYS, getEventCatalogFilterValues } from 'util/EventCatalogFilter';
import { NUMBER_DATE_FORMAT } from 'util/Format';

export interface AppliedEventFilter {
  key: string;
  label: string;
  value: string;
  displayValue: string;
}

interface AppliedEventFiltersData {
  filters: AppliedEventFilter[];
  removeFilter: (filter: AppliedEventFilter) => void;
  clearFilters: () => void;
}

const MULTI_VALUE_KEYS: string[] = ['type', 'creditType', 'location'];

const getOptionName = (options: SelectOption[] | undefined, loading: boolean, id: string): string | undefined =>
  options?.find((option) => option.id === id)?.name ?? (loading ? undefined : id);

const useAppliedEventFilters = (): AppliedEventFiltersData => {
  const { search, pathname } = useLocation();
  const history = useHistory();
  const {
    events: { hideCountryFilter },
  } = useContext(ParametersContext);
  const { startTime, endTime, type, creditType, location, country, state, age } = useMemo(
    () => getEventCatalogFilterValues(search),
    [search]
  );

  const hasType = !!type.length;
  const hasCreditType = !!creditType.length;
  const hasLocation = !!location.length;
  const hasCountry = !!country;
  const hasAge = !!age;
  const { data: types, loading: typesLoading } = useRequest<SelectOption[]>(
    useMemo(() => (hasType ? EventService.getEventTypes : undefined), [hasType])
  );
  const { data: creditTypes, loading: creditTypesLoading } = useRequest<SelectOption[]>(
    useMemo(() => (hasCreditType ? EventRegistrationService.getCreditCategories : undefined), [hasCreditType])
  );
  const { data: locations, loading: locationsLoading } = useRequest<SelectOption[]>(
    useMemo(() => (hasLocation ? EventService.getEventLocations : undefined), [hasLocation])
  );
  const { data: countries, loading: countriesLoading } = useRequest<Country[]>(
    useMemo(() => (hasCountry ? UserService.getCountries : undefined), [hasCountry])
  );
  const { data: ages, loading: agesLoading } = useRequest<SelectOption[] | null>(
    useMemo(() => (hasAge ? EventService.getEventAgeRanges : undefined), [hasAge])
  );

  const filters: AppliedEventFilter[] = useMemo(() => {
    const result: AppliedEventFilter[] = [];
    const addFilter = (key: string, label: string, value: string, displayValue?: string) => {
      if (displayValue) {
        result.push({ key, label, value, displayValue });
      }
    };
    const states = countries?.find(({ id }) => id === country)?.states;

    if (startTime) {
      addFilter('startTime', 'Start Date', `${startTime}`, moment(startTime).format(NUMBER_DATE_FORMAT));
    }
    if (endTime) {
      addFilter('endTime', 'End Date', `${endTime}`, moment(endTime).format(NUMBER_DATE_FORMAT));
    }
    type.forEach((id) => addFilter('type', 'Event Type', id, getOptionName(types, typesLoading, id)));
    creditType.forEach((id) =>
      addFilter('creditType', 'Credit Type', id, getOptionName(creditTypes, creditTypesLoading, id))
    );
    location.forEach((id) => addFilter('location', 'Location', id, getOptionName(locations, locationsLoading, id)));
    if (country) {
      addFilter('country', 'Country', country, getOptionName(countries, countriesLoading, country));
    }
    if (state) {
      addFilter('state', 'State/Province', state, getOptionName(states, countriesLoading, state));
    }
    if (age) {
      addFilter('age', 'Age Range', age, getOptionName(ages ?? undefined, agesLoading, age));
    }
    return result;
  }, [
    startTime,
    endTime,
    type,
    creditType,
    location,
    country,
    state,
    age,
    types,
    typesLoading,
    creditTypes,
    creditTypesLoading,
    locations,
    locationsLoading,
    countries,
    countriesLoading,
    ages,
    agesLoading,
  ]);

  const removeFilter = useCallback(
    ({ key, value }: AppliedEventFilter) => {
      const params: URLSearchParams = new URLSearchParams(search);

      if (MULTI_VALUE_KEYS.includes(key)) {
        const remainingValues: string[] = (params.get(key) || '').split(',').filter((item) => item && item !== value);

        remainingValues.length ? params.set(key, remainingValues.join(',')) : params.delete(key);
      } else if (key === 'country') {
        params.set('country', '');
        params.delete('state');
      } else {
        params.delete(key);
      }
      history.replace({ pathname, search: `?${params.toString()}` });
    },
    [history, pathname, search]
  );

  const clearFilters = useCallback(() => {
    const params: URLSearchParams = new URLSearchParams(search);

    EVENT_CATALOG_FILTER_KEYS.forEach((key) => params.delete(key));
    if (!hideCountryFilter) {
      params.set('country', '');
    }
    history.replace({ pathname, search: `?${params.toString()}` });
  }, [history, pathname, search, hideCountryFilter]);

  return { filters, removeFilter, clearFilters };
};

export default useAppliedEventFilters;
